import { IPackageToInstall } from "../../components/installation/UpdateWizard";
import { PackageStatus } from "../../typings/Packaging";
import { IUpdateLog } from "../../typings/UpdateLog";
import { IPackageService } from "../package/IPackageService";
import { IImportJob, ImportType, IProxy } from "../proxy/IProxy";
import { IUpdateLogService } from "../updatelog/IUpdateLogService";

import { createGUID, sleep } from "../utils/Utils";
import { LicenseRequestService } from "./LicenseRequestService";
import { InvokeActionRequest, RetrieveByIdRequest, RetrieveRequestType } from "../proxy/ProxyTypes";
import { appInsights } from "../../components/solutions/SolutionInstaller";
import { WarningFormatter } from "./WarningFormatter";
import { IEventTelemetry, IExceptionTelemetry } from "@microsoft/applicationinsights-common";
import { ICustomProperties } from "@microsoft/applicationinsights-core-js";

let packagesToInstall: IPackageToInstall[] = [];
export class SolutionImporter {
    private _proxy: IProxy;
    private _logService: IUpdateLogService;
    private _packageService: IPackageService;
    private _licenseRequestService: LicenseRequestService;
    private _setPackagesToInstall: Function;
    private _changeCurrentPackage: Function;
    private _setUnfinishedUpgrade: Function;

    constructor(proxy: IProxy, packageService: IPackageService, logService: IUpdateLogService, setPackagesToInstall: Function, changeCurrentPackage: Function, setUnfinishedUpgrade: Function) {
        this._proxy = proxy;
        this._logService = logService;
        this._packageService = packageService;
        this._setPackagesToInstall = setPackagesToInstall;
        this._changeCurrentPackage = changeCurrentPackage;
        this._setUnfinishedUpgrade = setUnfinishedUpgrade;
        this._licenseRequestService = new LicenseRequestService(proxy);
    }

    handleImportUpdate = (importJob: IImportJob) => {
        for (let i = 0; i < packagesToInstall.length; i++) {
            const element = packagesToInstall[i];
            if (element.version.id === importJob.solutionName) {
                element.job = importJob;
            }
        }

        this._setPackagesToInstall(packagesToInstall.slice());
    }

    async importSolutions(selection: IPackageToInstall[], selectedImportType: ImportType, updateLog: IUpdateLog) {
        packagesToInstall = selection;

        // used as an aid to restart import after failure
        var status: IImportStatus = {
            selection: selection,
            importType: selectedImportType,
            updateLog: updateLog,
            currentIndex: 0
        };

        console.log("Importing solutions")

        for (let i = 0; i < packagesToInstall.length; i++) {
            status.currentIndex = i;

            if (packagesToInstall[i].job || packagesToInstall[i].status === PackageStatus.Canceled) {
                continue;
            }

            const importType = packagesToInstall[i].status === PackageStatus.New ? ImportType.Update : selectedImportType;
            const currentLogItem = updateLog.items.find(x => x.solutionId === packagesToInstall[i].version.id);

            // update selection to show active installment
            packagesToInstall[i].active = true;
            this._setPackagesToInstall(packagesToInstall.slice());

            const result = await this._packageService.downloadSolution(packagesToInstall[i].version.id, packagesToInstall[i].version.version);

            if (result.ok) {
                packagesToInstall[i].solution = result.content;

                const importJob = await this.importSolution(importType, packagesToInstall[i]);

                // show install result and hide progressbar
                packagesToInstall[i].active = false;
                this._setPackagesToInstall(packagesToInstall.slice());

                if (importJob.success) {
                    // request trial license for newly installed solution
                    const requestLicenseComplete = await this.requestLicense(packagesToInstall[i].version.id, packagesToInstall[i].status);

                    if (currentLogItem) {
                        currentLogItem.importJobId = packagesToInstall[i].job?.id;
                        currentLogItem.successfullyInstalled = true;
                        currentLogItem.errorMessage = WarningFormatter.formatImportResultAsString(requestLicenseComplete, packagesToInstall[i].job?.data);
                        this._logService.saveUpdateLog(updateLog);
                        this._changeCurrentPackage(packagesToInstall[i].version.id);
                    }

                    this.trackImport(importType, packagesToInstall[i]);
                } else {
                    // process must be discontinued when an import fails
                    const errorMessage = packagesToInstall[i].job?.errorText;
                    if (currentLogItem) {
                        currentLogItem.importJobId = packagesToInstall[i].job?.id;
                        currentLogItem.errorMessage = errorMessage;
                        currentLogItem.successfullyInstalled = false;
                        this._logService.saveUpdateLog(updateLog);
                    }
                    // 0x8004f046 => Uninstall for Stage and Upgrade failed
                    if (packagesToInstall[i].job?.errorCode === '0x8004f046') {
                        this._setUnfinishedUpgrade(packagesToInstall[i].version.id)
                    }

                    this.trackImportError(importType, packagesToInstall[i], errorMessage ? errorMessage : "");
                    return status;
                }

            } else {
                // no solution found, import process can't continue 
                packagesToInstall[i].job = {
                    solutionName: packagesToInstall[i].version.id,
                    version: packagesToInstall[i].version.version,
                    importType: importType,
                    errorText: 'Solution file could not be found.',
                    progress: 100,
                    active: false,
                    success: false
                };
                packagesToInstall[i].active = false;
                this._setPackagesToInstall(packagesToInstall.slice());
                if (currentLogItem) {
                    currentLogItem.successfullyInstalled = false;
                    currentLogItem.errorMessage = packagesToInstall[i]?.job?.errorText;
                }
                this._logService.saveUpdateLog(updateLog);
                this.trackImportError(importType, packagesToInstall[i], result.content);
                return status;
            }
        }
    }

    cancelImport() {
        const index = packagesToInstall.findIndex(x => x.active);
        const cancelFromIndex = index === -1 ? 0 : index + 1;
        for (let i = cancelFromIndex; i < packagesToInstall.length; i++) {
            packagesToInstall[i].status = PackageStatus.Canceled;
        }
        this._setPackagesToInstall(packagesToInstall.slice());
    }

    async requestLicense(id: string, status: PackageStatus | undefined): Promise<boolean> {
        let completed = true;

        if (status === PackageStatus.New) {
            completed = await this._licenseRequestService.requestTrialLicense(id);
        }

        for (let i = 0; i < packagesToInstall.length; i++) {
            const element = packagesToInstall[i];
            if (element.version.id === id) {
                if (element.job) {
                    element.job.trialRequestComplete = completed;
                }
            }
        }
        this._setPackagesToInstall(packagesToInstall.slice());

        return completed;
    }

    private async importSolution(importType: ImportType, selected: IPackageToInstall): Promise<IImportJob> {
        console.log(`Importing solution: ${selected.version.id}, version: ${selected.version.version}, import type: ${importType}`);

        const importJob = this.buildImportJob(selected.version, importType);

        try {
            
            const action = this.buildImportAction(importType, selected.solution, importJob.id!);
    
            const asyncActionResult = await this._proxy.Invoke('invokeAction', action);
    
            const asyncImportOperationId = asyncActionResult.AsyncOperationId ?? asyncActionResult.asyncOperationId;
    
            while (true) {
                const asyncImportOperation = await this.getAsyncOperation(asyncImportOperationId);
    
                const updateImportJob = await this.getImportJobById(importJob);
    
                this.handleImportUpdate(updateImportJob);
    
                if (asyncImportOperation.statuscode == 30) { // succeeded
                    importJob.active = false;
                    importJob.success = true;
    
                    return importJob;
                }
                else if (asyncImportOperation.statuscode == 31 || asyncImportOperation.statuscode == 32) { // 31 = failed, 32 = canceled
                    importJob.active = false;
                    importJob.success = false;
                    importJob.errorCode = asyncImportOperation.errorcode;
                    importJob.errorText = asyncImportOperation.friendlymessage;
    
                    return importJob;
                }
    
                await sleep(3000);
            }
        }
        catch (error: Error | any) {
            importJob.active = false;
            importJob.success = false;
            importJob.errorText = error.message || 'An error occurred during the import process.';

            return importJob;
        }
    }

    private buildImportJob(solutionVersion: { id: string, version: string }, importType: ImportType): IImportJob {
        return {
            id: createGUID(),
            solutionName: solutionVersion.id,
            version: solutionVersion.version,
            importType: importType,
            progress: 0,
            active: true,
            success: false
        };
    }

    private buildImportAction(importType: ImportType, solution: string, importJobKey: string): InvokeActionRequest {
        let actionName: string;
        let body: string;
        if (importType === ImportType.Update) {
            actionName = 'ImportSolutionAsync';
            body = JSON.stringify({
                HoldingSolution: false,
                OverwriteUnmanagedCustomizations: false,
                PublishWorkflows: true,
                CustomizationFile: solution,
                ImportJobId: importJobKey
            });
        } else {
            actionName = 'StageAndUpgradeAsync';
            body = JSON.stringify({
                OverwriteUnmanagedCustomizations: false,
                PublishWorkflows: true,
                CustomizationFile: solution,
                ImportJobId: importJobKey
            });
        }

        const action: InvokeActionRequest = {
            actionName: actionName,
            data: body,
            apiVersion: 'v9.0',
            isBound: true,
            entitySetName: actionName
        };

        return action;
    }

    private async getAsyncOperation(asyncOperationId: string): Promise<any> {
        try {
            const retrieveAsyncOperation: RetrieveByIdRequest = {
                id: asyncOperationId ?? '',
                type: RetrieveRequestType.ById,
                entitySetName: 'asyncoperations',
                apiVersion: "v9.0"
            };

            const response = await this._proxy.Invoke('executeQuery', retrieveAsyncOperation);

            return response;
        }
        catch (error) {
            console.error("Error while retrieving import job by ID: ", error);
        }

        return {};
    }

    // an error should not occur, so we don't throw an error here. The job takes a while to register, so at first you will likely get a not found error
    private async getImportJobById(importJob: IImportJob): Promise<IImportJob> {
        try {
            const retrieveImportJob: RetrieveByIdRequest = {
                id: importJob.id ?? '',
                type: RetrieveRequestType.ById,
                entitySetName: 'importjobs',
                apiVersion: "v9.0"
            };

            const response = await this._proxy.Invoke('executeQuery', retrieveImportJob);

            if (!response.error) {
                const formatted = this.formatImportJobRequest(response);

                importJob.progress = response.progress;
                importJob.data = response.data;
                importJob.success = formatted.succeeded;
            }
            else {
                console.log("Error while fetching solution import progress: " + response.error.message);
            }
        }
        catch (error) {
            // NOTE: Should not throw error, but there might be a network issue
            // or the import job is not yet created which causes the retrieve to fail. 
            // In both cases we want to continue polling for the job progress.
            console.error("Error while retrieving import job by ID: ", error);
        }

        return importJob;
    }

    private formatImportJobRequest(result: any) {
        let parser = new DOMParser();
        let xmlDoc = parser.parseFromString(result.data, "text/xml");

        let attributes = xmlDoc.getElementsByTagName("importexportxml")[0].attributes;
        let value = {
            processed: false,
            succeeded: false,
            status: ''
        };

        for (let i = 0; i < attributes.length; i++) {
            switch (attributes[i].nodeName) {
                case "processed":
                    value.processed = attributes[i].nodeValue === 'false' ? false : true;
                    break;
                case "succeeded":
                    value.succeeded = attributes[i].nodeValue || '' in ['succeeded', 'success'] ? true : false;
                    break;
                case "status":
                    value.status = attributes[i].nodeValue || '';
                    break;
                default:
                    break;
            }
        }

        return value;
    }

    private trackImportError(importType: ImportType, selected: IPackageToInstall, message: string) {
        const exception: IExceptionTelemetry = {
            error: {
                name: 'Solution Import Failed',
                message: message
            }
        };
        const customProperties: ICustomProperties = {
            organizationId: this._proxy.getAuthorizationData().organizationId,
            solution: `${selected.version.id} ${selected.version.version}`,
            importType: ImportType.Update === importType ? 'Update' : 'Upgrade',
            importJobId: selected.job?.id
        };
        appInsights.trackException(exception, customProperties);
    }

    private trackImport(importType: ImportType, selected: IPackageToInstall) {
        const event: IEventTelemetry = {
            name: 'Solution Import Success'
        };
        const customProperties: ICustomProperties = {
            organizationId: this._proxy.getAuthorizationData().organizationId,
            solution: `${selected.version.id} ${selected.version.version}`,
            importType: ImportType.Update === importType ? 'Update' : 'Upgrade',
            importJobId: selected.job?.id
        };
        appInsights.trackEvent(event, customProperties)
    }

}
export interface IImportStatus {
    selection: IPackageToInstall[];
    importType: ImportType;
    updateLog: IUpdateLog;
    currentIndex: number;
}